import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Heart, Calendar, User, ArrowLeft, Search, Tag, Clock, TrendingUp, Bookmark, 
  Eye, MessageCircle, Share2, Plus, Send, Edit, Trash2, Check, X, LogIn, LogOut,
  CheckCircle, FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { useTheme, themes } from './ThemeProvider';
import { useAuthContext } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogFooter, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import apiClient from '../lib/api-client';
import { toast } from 'sonner';
import { RichTextEditor } from './RichTextEditor';
import { shouldUseDemoMode } from '../utils/demoMode';

interface Blog {
  _id: string;
  id?: number;
  title: string;
  excerpt: string;
  content: string;
  slug: string;
  author: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | string;
  guestAuthor?: {
    name: string;
    email: string;
    bio?: string;
    website?: string;
  };
  authorType: 'admin' | 'guest';
  date?: string;
  createdAt?: string;
  publishedAt?: string;
  category: string;
  readTime?: string;
  readingTime?: number;
  image?: string;
  featuredImage?: {
    url: string;
    alt?: string;
  };
  tags: string[];
  views?: number;
  likes?: string[];
  comments?: any[];
  isFeatured?: boolean;
  status?: string;
}

interface BlogsPageProps {
  onBack: () => void;
  isSubdomain?: boolean;
}

const blogPosts: Blog[] = [
  {
    _id: '1',
    id: 1,
    title: "10 Tips for Creating the Perfect Matrimonial Profile",
    excerpt: "Learn how to make your profile stand out and attract the right matches with these expert tips.",
    content: "Creating an engaging profile is crucial for success in online matrimony...",
    slug: "perfect-matrimonial-profile",
    author: 'Dr. Priya Sharma' as any,
    authorType: 'guest',
    guestAuthor: {
      name: 'Dr. Priya Sharma',
      email: 'priya@example.com'
    },
    date: "2024-11-20",
    category: "profile_tips",
    readTime: "5 min read",
    readingTime: 5,
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop",
    tags: ["Profile", "Tips", "Success"],
    views: 1250,
    likes: [],
    comments: [],
    status: 'published'
  },
  {
    _id: '2',
    id: 2,
    title: "Understanding Compatibility: Beyond the Basics",
    excerpt: "Discover the key factors that contribute to long-term relationship success and compatibility.",
    content: "Compatibility is more than just shared interests...",
    slug: "understanding-compatibility",
    author: 'Rajesh Kumar' as any,
    authorType: 'guest',
    guestAuthor: {
      name: 'Rajesh Kumar',
      email: 'rajesh@example.com'
    },
    date: "2024-11-18",
    category: "relationship_advice",
    readTime: "7 min read",
    readingTime: 7,
    image: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=800&h=400&fit=crop",
    tags: ["Compatibility", "Relationships", "Love"],
    views: 980,
    likes: [],
    comments: [],
    status: 'published'
  },
  {
    _id: '3',
    id: 3,
    title: "Success Stories: Real Couples Who Found Love Online",
    excerpt: "Read inspiring stories from couples who found their perfect match through our platform.",
    content: "Every love story is unique and special...",
    slug: "success-stories-online",
    author: 'Anjali Verma' as any,
    authorType: 'guest',
    guestAuthor: {
      name: 'Anjali Verma',
      email: 'anjali@example.com'
    },
    date: "2024-11-15",
    category: "success_stories",
    readTime: "10 min read",
    readingTime: 10,
    image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&h=400&fit=crop",
    tags: ["Success", "Love Stories", "Testimonials"],
    views: 2150,
    likes: [],
    comments: [],
    status: 'published'
  }
];

export default function BlogsPage({ onBack, isSubdomain }: BlogsPageProps) {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { user, isAuthenticated, logout } = useAuthContext();
  
  const isSubdomainMode = isSubdomain || window.location.hostname.startsWith('blogs.') || window.location.hostname.includes('blogs.localhost');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<Blog | null>(null);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [relatedBlogs, setRelatedBlogs] = useState<Blog[]>([]);
  const [popularBlogs, setPopularBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);

  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (!selectedPost) {
      setScrollProgress(0);
      return;
    }

    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const progress = (window.scrollY / totalHeight) * 100;
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [selectedPost]);

  // Guest submission form
  const [guestBlogOpen, setGuestBlogOpen] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [commentName, setCommentName] = useState('');
  const [commentEmail, setCommentEmail] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [guestBlogForm, setGuestBlogForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    guestAuthor: {
      name: '',
      email: '',
      bio: '',
      website: ''
    }
  });

  // Admin states
  const [adminStatus, setAdminStatus] = useState<'published' | 'draft' | 'pending'>('published');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    featuredImage: '',
    status: 'published' as 'published' | 'draft',
    isFeatured: false
  });

  const categories = [
    'All', 
    'Relationship Advice', 
    'Wedding Planning', 
    'Marriage Tips', 
    'Dating Tips', 
    'Lifestyle', 
    'Success Stories', 
    'Festivals', 
    'Fashion', 
    'Health'
  ];

  const categoryMap: Record<string, string> = {
    'Relationship Advice': 'relationship_advice',
    'Wedding Planning': 'wedding_planning',
    'Marriage Tips': 'marriage_tips',
    'Dating Tips': 'dating_tips',
    'Lifestyle': 'lifestyle',
    'Success Stories': 'success_stories',
    'Festivals': 'festivals',
    'Fashion': 'fashion',
    'Health': 'health'
  };

  const getCategoryDisplay = (category: string) => {
    const display = Object.entries(categoryMap).find(([key, val]) => val === category)?.[0];
    return display || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const fetchBlogs = async (status = 'published') => {
    try {
      setLoading(true);
      const response = await apiClient.getBlogs({ status, limit: 100 });
      if (response.success) {
        setBlogs(response.data.blogs || []);
      }
    } catch (error: any) {
      // Fallback to mock data filter
      const matchedMock = blogPosts.filter(p => {
        const postStatus = p.status || 'published';
        return postStatus === status;
      });
      setBlogs(matchedMock.length > 0 ? matchedMock : blogPosts);
    } finally {
      setLoading(false);
    }
  };

  const fetchPopularBlogs = async () => {
    try {
      const response = await apiClient.getBlogs({ status: 'published', limit: 5 });
      if (response.success) {
        const sorted = [...(response.data.blogs || [])].sort((a, b) => (b.views || 0) - (a.views || 0));
        setPopularBlogs(sorted.slice(0, 5));
      }
    } catch (error) {
      const sorted = [...blogPosts].sort((a, b) => (b.views || 0) - (a.views || 0));
      setPopularBlogs(sorted.slice(0, 5));
    }
  };

  const fetchRelatedBlogs = async (blog: Blog) => {
    try {
      const response = await apiClient.getBlogs({ 
        category: blog.category, 
        status: 'published', 
        limit: 3 
      });
      if (response.success) {
        const filtered = (response.data.blogs || []).filter((b: Blog) => b._id !== blog._id).slice(0, 3);
        setRelatedBlogs(filtered);
      }
    } catch (error) {
      const filtered = blogPosts.filter(b => 
        b._id !== blog._id && b.category === blog.category
      ).slice(0, 3);
      setRelatedBlogs(filtered);
    }
  };

  useEffect(() => {
    fetchBlogs(user?.role === 'admin' ? adminStatus : 'published');
    fetchPopularBlogs();
  }, [adminStatus, user]);

  useEffect(() => {
    const loadBlogBySlug = async () => {
      if (slug) {
        try {
          const response = await apiClient.getBlog(slug);
          if (response.success && response.data.blog) {
            setSelectedPost(response.data.blog);
            return;
          }
        } catch (error) {
          console.error('Failed to fetch blog by slug:', error);
        }

        const foundLocal = blogs.find(b => b.slug === slug) || 
                           blogPosts.find(b => b.slug === slug);
        if (foundLocal) {
          setSelectedPost(foundLocal);
        } else {
          toast.error('Blog article not found');
          navigate('/blogs');
        }
      } else {
        setSelectedPost(null);
      }
    };

    loadBlogBySlug();
  }, [slug, blogs, navigate]);

  useEffect(() => {
    if (selectedPost) {
      fetchRelatedBlogs(selectedPost);
    }
  }, [selectedPost]);

  const handleViewBlog = (blog: Blog) => {
    navigate(`/blogs/${blog.slug}`);
  };

  const handleLikeBlog = async () => {
    if (!selectedPost || !user) {
      toast.error('Please login to like blogs');
      return;
    }
    try {
      await apiClient.likeBlog(selectedPost._id);
      toast.success('Blog liked successfully');
      const response = await apiClient.getBlog(selectedPost.slug);
      if (response.success) {
        setSelectedPost(response.data.blog);
      }
    } catch (error) {
      toast.error('Failed to like blog');
    }
  };

  const handleSubmitGuestBlog = async () => {
    if (!guestBlogForm.title || !guestBlogForm.excerpt || !guestBlogForm.content || !guestBlogForm.category) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const tagsArray = guestBlogForm.tags.split(',').map(t => t.trim()).filter(Boolean);
      const categoryValue = categoryMap[guestBlogForm.category] || 'other';

      await apiClient.submitGuestBlog({
        title: guestBlogForm.title,
        excerpt: guestBlogForm.excerpt,
        content: guestBlogForm.content,
        category: categoryValue,
        tags: tagsArray,
        guestAuthor: guestBlogForm.guestAuthor
      });

      toast.success('Guest blog submitted successfully! It will be reviewed by admin.');
      setGuestBlogOpen(false);
      setGuestBlogForm({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        tags: '',
        guestAuthor: { name: '', email: '', bio: '', website: '' }
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to submit guest blog');
    }
  };
  
  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentContent.trim()) return;

    if (!isAuthenticated && !shouldUseDemoMode()) {
      toast.error('Please login to leave a comment');
      return;
    }

    try {
      setSubmittingComment(true);
      const nameToUse = isAuthenticated ? `${user?.firstName} ${user?.lastName}` : (commentName || 'Guest Reader');
      const emailToUse = isAuthenticated ? user?.email : (commentEmail || 'guest@example.com');

      if (shouldUseDemoMode()) {
        const newComment = {
          _id: `comment-mock-${Date.now()}`,
          name: nameToUse,
          email: emailToUse,
          content: commentContent,
          isApproved: true,
          createdAt: new Date().toISOString()
        };

        const updatedPost = {
          ...selectedPost!,
          comments: [...(selectedPost?.comments || []), newComment]
        };

        setSelectedPost(updatedPost);
        setBlogs(prev => prev.map(b => b._id === selectedPost!._id ? updatedPost : b));

        toast.success('Comment added successfully!');
        setCommentContent('');
        setCommentName('');
        setCommentEmail('');
      } else {
        const response = await apiClient.addComment(selectedPost!._id, commentContent);
        if (response.success) {
          toast.success('Comment submitted! It will appear once approved by an admin.');
          const refreshed = await apiClient.getBlog(selectedPost!.slug);
          if (refreshed.success) {
            setSelectedPost(refreshed.data.blog);
          }
          setCommentContent('');
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Admin Action Handlers
  const handleOpenCreate = () => {
    setEditingBlog(null);
    setBlogForm({
      title: '',
      excerpt: '',
      content: '',
      category: 'relationship_advice',
      tags: '',
      featuredImage: '',
      status: 'published',
      isFeatured: false
    });
    setEditorOpen(true);
  };

  const handleOpenEdit = (blog: Blog, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title,
      excerpt: blog.excerpt,
      content: blog.content,
      category: blog.category,
      tags: blog.tags?.join(', ') || '',
      featuredImage: blog.featuredImage?.url || blog.image || '',
      status: (blog.status as any) || 'published',
      isFeatured: blog.isFeatured || false
    });
    setEditorOpen(true);
  };

  const handleSaveBlog = async () => {
    if (!blogForm.title || !blogForm.content || !blogForm.category) {
      toast.error('Please fill in title, content, and category');
      return;
    }

    try {
      const tagsArray = blogForm.tags.split(',').map(t => t.trim()).filter(Boolean);
      const data = {
        title: blogForm.title,
        excerpt: blogForm.excerpt || blogForm.title,
        content: blogForm.content,
        category: blogForm.category,
        tags: tagsArray,
        featuredImage: blogForm.featuredImage ? { url: blogForm.featuredImage } : undefined,
        status: blogForm.status,
        isFeatured: blogForm.isFeatured
      };

      if (editingBlog) {
        await apiClient.updateBlog(editingBlog._id, data);
        toast.success('Blog updated successfully');
      } else {
        await apiClient.createBlog(data);
        toast.success('Blog created successfully');
      }

      setEditorOpen(false);
      fetchBlogs(user?.role === 'admin' ? adminStatus : 'published');
      
      if (selectedPost && editingBlog && selectedPost._id === editingBlog._id) {
        const response = await apiClient.getBlog(selectedPost.slug);
        if (response.success) {
          setSelectedPost(response.data.blog);
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save blog');
    }
  };

  const handleDeleteBlog = async (blogId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await apiClient.deleteBlog(blogId);
      toast.success('Blog deleted successfully');
      fetchBlogs(user?.role === 'admin' ? adminStatus : 'published');
      if (selectedPost?._id === blogId) {
        navigate('/blogs');
      }
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const handleApproveBlog = async (blogId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await apiClient.approveBlog(blogId);
      toast.success('Blog approved and published');
      fetchBlogs(adminStatus);
    } catch (error) {
      toast.error('Failed to approve blog');
    }
  };

  const handleRejectBlog = async (blogId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const reason = window.prompt('Enter rejection reason:');
    if (reason === null) return;
    try {
      await apiClient.rejectBlog(blogId, reason);
      toast.success('Blog submission rejected');
      fetchBlogs(adminStatus);
    } catch (error) {
      toast.error('Failed to reject blog');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      fetchBlogs('published');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const getAuthorName = (blog: Blog) => {
    if (blog.authorType === 'guest' && blog.guestAuthor) {
      return blog.guestAuthor.name;
    }
    if (typeof blog.author === 'object' && blog.author) {
      return `${blog.author.firstName} ${blog.author.lastName}`;
    }
    return 'Anonymous';
  };

  const getImageUrl = (blog: Blog) => {
    if (blog.featuredImage?.url) return blog.featuredImage.url;
    if (blog.image) return blog.image;
    
    const categoryImages: Record<string, string[]> = {
      relationship_advice: [
        'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=800&h=400&fit=crop'
      ],
      wedding_planning: [
        'https://images.unsplash.com/photo-1519741497674-611481863552?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=800&h=400&fit=crop'
      ],
      marriage_tips: [
        'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?w=800&h=400&fit=crop'
      ],
      dating_tips: [
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1501901909934-e1c165b7c645?w=800&h=400&fit=crop'
      ],
      lifestyle: [
        'https://images.unsplash.com/photo-1511185307590-3c2d346b7b2a?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=400&fit=crop'
      ],
      success_stories: [
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=800&h=400&fit=crop'
      ],
      festivals: [
        'https://images.unsplash.com/photo-1514222134-b57cbb8ce073?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1504609773096-104ff2c73ba4?w=800&h=400&fit=crop'
      ],
      fashion: [
        'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=800&h=400&fit=crop'
      ],
      health: [
        'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&h=400&fit=crop'
      ],
      other: [
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop'
      ]
    };

    const category = blog.category || 'other';
    const images = categoryImages[category] || categoryImages.other;
    const hashInput = blog._id || blog.title || '';
    let hash = 0;
    for (let i = 0; i < hashInput.length; i++) {
      hash = hashInput.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % images.length;
    return images[index];
  };

  const filteredPosts = blogs.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
    const matchesCategory = !selectedCategory || selectedCategory === 'All' || 
                            getCategoryDisplay(post.category) === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (selectedPost) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/blogs')}>
              <ArrowLeft className="size-4 mr-2" />
              Back to Blogs
            </Button>
            <div className="flex items-center gap-2">
              <span className={`text-xl font-bold ${currentTheme.primaryText}`}>Atootbandan Blog</span>
            </div>
          </div>
          {/* Scroll progress bar */}
          <div className="w-full h-1 bg-gray-150 absolute bottom-0 left-0 right-0 overflow-hidden">
            <div 
              className={`h-full ${currentTheme.gradient || 'bg-rose-600'} transition-all duration-75`}
              style={{ width: `${scrollProgress}%` }}
            />
          </div>
        </header>

        {/* Blog Post Detail */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <article className="lg:col-span-2">
              <img 
                src={getImageUrl(selectedPost)} 
                alt={selectedPost.title}
                className="w-full h-96 object-cover rounded-2xl mb-8 shadow-lg"
              />
              
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <Badge className={`${currentTheme.gradient} text-white mb-4`}>
                  {getCategoryDisplay(selectedPost.category)}
                </Badge>
                
                <h1 className="text-4xl font-bold mb-4 text-gray-900">{selectedPost.title}</h1>
                
                <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8 pb-8 border-b">
                  <div className="flex items-center gap-2">
                    <User className="size-4" />
                    <span>{getAuthorName(selectedPost)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="size-4" />
                    <span>{new Date(selectedPost.publishedAt || selectedPost.createdAt || selectedPost.date || '').toLocaleDateString('en-IN', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="size-4" />
                    <span>{selectedPost.readingTime || 5} min read</span>
                  </div>
                  {selectedPost.views && (
                    <div className="flex items-center gap-2">
                      <Eye className="size-4" />
                      <span>{selectedPost.views} views</span>
                    </div>
                  )}
                </div>

                {/* Engagement & Admin Actions */}
                <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleLikeBlog}
                    className="gap-2"
                  >
                    <Heart className={`size-4 ${selectedPost.likes?.includes(user?._id || '') ? 'fill-rose-500 text-rose-500' : ''}`} />
                    {selectedPost.likes?.length || 0} Likes
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2">
                    <MessageCircle className="size-4" />
                    {selectedPost.comments?.length || 0} Comments
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Share2 className="size-4" />
                    Share
                  </Button>
                  {user?.role === 'admin' && (
                    <>
                      <Button variant="outline" size="sm" className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={(e) => handleOpenEdit(selectedPost, e)}>
                        <Edit className="size-4" />
                        Edit Post
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm" className="gap-2">
                            <Trash2 className="size-4" />
                            Delete Post
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this blog post? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={(e) => handleDeleteBlog(selectedPost._id, e)} className="bg-red-600 hover:bg-red-700 text-white">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>

                <div className="prose max-w-none mb-8">
                  <p className="text-xl text-gray-700 leading-relaxed mb-6 font-medium">{selectedPost.excerpt}</p>
                  <div 
                    className="wp-content-prose animate-fadeIn"
                    dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                  />
                </div>

                {selectedPost.tags && selectedPost.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-8 border-t">
                    {selectedPost.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="gap-1">
                        <Tag className="size-3" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Author Info Card */}
                <div className="mt-8 p-6 rounded-2xl bg-gray-50 border border-gray-150 flex flex-col sm:flex-row items-center sm:items-start gap-4">
                  <div className={`size-16 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0 ${currentTheme.gradient}`}>
                    {getAuthorName(selectedPost).charAt(0)}
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-center sm:justify-start">
                      <h4 className="font-bold text-gray-900 text-lg">{getAuthorName(selectedPost)}</h4>
                      <Badge variant="secondary" className="w-fit mx-auto sm:mx-0">
                        {selectedPost.authorType === 'admin' ? 'Editorial Team' : 'Guest Contributor'}
                      </Badge>
                    </div>
                    <p className="text-gray-600 mt-2 text-sm leading-relaxed">
                      {selectedPost.authorType === 'guest' && selectedPost.guestAuthor?.bio 
                        ? selectedPost.guestAuthor.bio 
                        : "Passionate about helping people find their perfect life partner and sharing relationship guidance to build strong, happy marriages."}
                    </p>
                    {selectedPost.authorType === 'guest' && selectedPost.guestAuthor?.website && (
                      <a 
                        href={selectedPost.guestAuthor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-xs font-semibold mt-3 inline-block ${currentTheme.primaryText} hover:underline`}
                      >
                        Visit Website →
                      </a>
                    )}
                  </div>
                </div>

                {/* Style tag for WordPress-like typography styling */}
                <style dangerouslySetInnerHTML={{ __html: `
                  .wp-content-prose {
                    font-family: 'Outfit', system-ui, -apple-system, sans-serif;
                    font-size: 1.125rem;
                    line-height: 1.85;
                    color: #374151;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                  }
                  .wp-content-prose p {
                    margin-bottom: 1.5rem;
                  }
                  .wp-content-prose strong, .wp-content-prose b {
                    font-weight: 700;
                    color: #1f2937;
                  }
                  .wp-content-prose h1, 
                  .wp-content-prose h2, 
                  .wp-content-prose h3, 
                  .wp-content-prose h4,
                  .wp-content-prose h5 {
                    font-family: 'Outfit', system-ui, -apple-system, sans-serif;
                    font-weight: 700;
                    color: #111827;
                    line-height: 1.3;
                  }
                  .wp-content-prose h2 {
                    font-size: 1.75rem;
                    border-left: 4px solid #f43f5e;
                    padding-left: 0.75rem;
                    margin-top: 2.5rem;
                    margin-bottom: 1.25rem;
                  }
                  .wp-content-prose h3 {
                    font-size: 1.375rem;
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                    color: #1f2937;
                  }
                  .wp-content-prose h4 {
                    font-size: 1.15rem;
                    margin-top: 1.5rem;
                    margin-bottom: 0.75rem;
                  }
                  .wp-content-prose blockquote {
                    border-left: 4px solid #f43f5e;
                    background-color: #fff1f2;
                    padding: 1.25rem 1.5rem;
                    margin: 2rem 0;
                    font-style: italic;
                    border-radius: 0 0.75rem 0.75rem 0;
                    color: #4b5563;
                  }
                  .wp-content-prose blockquote p:last-child {
                    margin-bottom: 0;
                  }
                  .wp-content-prose ul {
                    list-style-type: disc;
                    padding-left: 1.75rem;
                    margin-bottom: 1.5rem;
                    margin-top: 0.5rem;
                  }
                  .wp-content-prose ol {
                    list-style-type: decimal;
                    padding-left: 1.75rem;
                    margin-bottom: 1.5rem;
                    margin-top: 0.5rem;
                  }
                  .wp-content-prose li {
                    margin-bottom: 0.625rem;
                    padding-left: 0.25rem;
                    line-height: 1.75;
                  }
                  .wp-content-prose li::marker {
                    color: #f43f5e;
                    font-weight: 600;
                  }
                  .wp-content-prose a {
                    color: #e11d48;
                    font-weight: 600;
                    text-decoration: underline;
                    text-decoration-color: rgba(225, 29, 72, 0.3);
                    text-underline-offset: 3px;
                    transition: all 0.2s ease;
                  }
                  .wp-content-prose a:hover {
                    color: #be123c;
                    text-decoration-color: #be123c;
                  }
                  .wp-content-prose img {
                    border-radius: 1rem;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
                    margin: 2rem auto;
                    max-width: 100%;
                    height: auto;
                    display: block;
                  }
                  /* Table styling */
                  .wp-content-prose table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 1.75rem 0;
                    font-size: 0.95rem;
                    border-radius: 0.75rem;
                    overflow: hidden;
                    border: 1px solid #e5e7eb;
                  }
                  .wp-content-prose thead {
                    background: linear-gradient(135deg, #f43f5e, #ec4899);
                  }
                  .wp-content-prose thead th {
                    color: #ffffff;
                    font-weight: 700;
                    text-align: left;
                    padding: 0.875rem 1.125rem;
                    font-size: 0.875rem;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                    border: none;
                  }
                  .wp-content-prose tbody tr {
                    border-bottom: 1px solid #f3f4f6;
                    transition: background-color 0.15s ease;
                  }
                  .wp-content-prose tbody tr:nth-child(even) {
                    background-color: #fafafa;
                  }
                  .wp-content-prose tbody tr:hover {
                    background-color: #fff1f2;
                  }
                  .wp-content-prose tbody td {
                    padding: 0.8rem 1.125rem;
                    border: none;
                    color: #374151;
                    vertical-align: top;
                    line-height: 1.6;
                  }
                  .wp-content-prose tbody td strong {
                    color: #111827;
                  }
                  /* Responsive table */
                  @media (max-width: 640px) {
                    .wp-content-prose table {
                      font-size: 0.85rem;
                    }
                    .wp-content-prose thead th,
                    .wp-content-prose tbody td {
                      padding: 0.625rem 0.75rem;
                    }
                    .wp-content-prose h2 {
                      font-size: 1.5rem;
                    }
                    .wp-content-prose h3 {
                      font-size: 1.2rem;
                    }
                  }
                `}} />

                {/* WordPress-like Comments stream */}
                {(() => {
                  const displayComments = (selectedPost.comments || []).filter(c => 
                    user?.role === 'admin' || c.isApproved === true || c.email === user?.email
                  );

                  const parentComments: any[] = [];
                  const commentRepliesMap: Record<string, any[]> = {};

                  displayComments.forEach(c => {
                    const match = c.content?.match(/^Reply to ([^:]+):\s*(.*)/);
                    if (match) {
                      const parentName = match[1];
                      const replyText = match[2];
                      const parent = parentComments.find(p => p.name === parentName);
                      if (parent) {
                        if (!commentRepliesMap[parent._id]) {
                          commentRepliesMap[parent._id] = [];
                        }
                        commentRepliesMap[parent._id].push({
                          ...c,
                          content: replyText
                        });
                        return;
                      }
                    }
                    parentComments.push(c);
                  });

                  return (
                    <div className="mt-12 bg-white rounded-2xl p-8 shadow-sm border border-gray-150">
                      <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2 border-b pb-4">
                        <MessageCircle className="size-6 text-rose-500 fill-rose-50" />
                        Comments ({parentComments.length + Object.values(commentRepliesMap).flat().length})
                      </h3>

                      <div className="space-y-6 mb-10 max-h-[600px] overflow-y-auto pr-2">
                        {parentComments.length > 0 ? (
                          parentComments.map((comment) => (
                            <div key={comment._id} className="space-y-4 border-b pb-6 last:border-0 last:pb-0">
                              <div className="flex items-start gap-4">
                                <div className={`size-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 bg-gradient-to-r ${currentTheme.gradient || 'from-rose-500 to-pink-500'}`}>
                                  {comment.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-gray-900 text-sm sm:text-base">{comment.name}</span>
                                      {(comment.email === selectedPost.guestAuthor?.email || (typeof selectedPost.author === 'object' && comment.email === selectedPost.author?.email)) && (
                                        <Badge className="bg-rose-50 text-rose-600 text-[10px] px-2 py-0.5 border-rose-100 font-semibold">Author</Badge>
                                      )}
                                      {(comment.email === 'admin@test.com' || comment.name.includes('Admin')) && (
                                        <Badge className="bg-gray-100 text-gray-800 text-[10px] px-2 py-0.5 font-semibold">Admin</Badge>
                                      )}
                                      {!comment.isApproved && (
                                        <Badge variant="outline" className="text-amber-600 border-amber-200 text-[10px] bg-amber-50">Pending Review</Badge>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-400">
                                      {new Date(comment.createdAt || '').toLocaleDateString('en-IN', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <p className="text-gray-700 text-sm mt-2 leading-relaxed bg-gray-50/50 p-3 rounded-xl border border-gray-100">
                                    {comment.content}
                                  </p>
                                </div>
                              </div>

                              {commentRepliesMap[comment._id] && commentRepliesMap[comment._id].map((reply) => (
                                <div key={reply._id} className="pl-12 flex items-start gap-4">
                                  <div className={`size-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 bg-gray-700`}>
                                    {reply.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-gray-900 text-sm">{reply.name}</span>
                                        <Badge className="bg-gray-100 text-gray-800 text-[10px] px-2 py-0.5 font-semibold">Admin Team</Badge>
                                      </div>
                                      <span className="text-xs text-gray-400">
                                        {new Date(reply.createdAt || '').toLocaleDateString('en-IN', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                      </span>
                                    </div>
                                    <p className="text-gray-700 text-sm mt-2 leading-relaxed bg-rose-50/20 p-3 rounded-xl border border-rose-100/50">
                                      {reply.content}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-400">
                            <MessageCircle className="size-12 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm font-medium">Be the first to comment on this article!</p>
                          </div>
                        )}
                      </div>

                      <div className="border-t pt-8">
                        <h4 className="text-lg font-bold text-gray-900 mb-2">Leave a Reply</h4>
                        <p className="text-xs text-gray-400 mb-6">Your email address will not be published. Required fields are marked *</p>

                        <form onSubmit={handleAddComment} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="comment-text" className="font-semibold text-gray-700">Comment *</Label>
                            <Textarea
                              id="comment-text"
                              rows={4}
                              required
                              value={commentContent}
                              onChange={(e) => setCommentContent(e.target.value)}
                              placeholder="Share your thoughts, advice, or feedback..."
                              className="rounded-xl border-gray-200 focus-visible:ring-rose-500 shadow-sm"
                            />
                          </div>

                          {!isAuthenticated ? (
                            shouldUseDemoMode() ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="comment-name" className="font-semibold text-gray-700">Name *</Label>
                                  <Input
                                    id="comment-name"
                                    required
                                    value={commentName}
                                    onChange={(e) => setCommentName(e.target.value)}
                                    placeholder="e.g. John Doe"
                                    className="rounded-xl border-gray-200 shadow-sm h-11"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="comment-email" className="font-semibold text-gray-700">Email *</Label>
                                  <Input
                                    id="comment-email"
                                    type="email"
                                    required
                                    value={commentEmail}
                                    onChange={(e) => setCommentEmail(e.target.value)}
                                    placeholder="e.g. john@example.com"
                                    className="rounded-xl border-gray-200 shadow-sm h-11"
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between flex-wrap gap-4">
                                <span className="text-sm text-amber-800 font-medium">You must be logged in to submit comments.</span>
                                <Button type="button" variant="outline" size="sm" onClick={() => navigate('/login')} className="border-amber-300 hover:bg-amber-100 text-amber-900">
                                  Log In to Comment
                                </Button>
                              </div>
                            )
                          ) : (
                            <div className="text-sm text-gray-500 bg-gray-50 px-4 py-2.5 rounded-xl border flex items-center justify-between">
                              <span>Commenting as <strong className="text-gray-800">{user?.firstName} {user?.lastName}</strong> ({user?.email})</span>
                            </div>
                          )}

                          {(!commentName && !isAuthenticated && !shouldUseDemoMode()) ? null : (
                            <Button
                              type="submit"
                              disabled={submittingComment || !commentContent.trim()}
                              className={`${currentTheme.gradient || 'bg-rose-600'} text-white font-semibold rounded-xl px-8 h-11 shadow-md hover:shadow-lg transition-all`}
                            >
                              {submittingComment ? 'Submitting...' : 'Post Comment'}
                            </Button>
                          )}
                        </form>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </article>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Popular Posts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="size-5 text-rose-500" />
                    Popular Posts
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {popularBlogs.map((blog) => (
                    <div 
                      key={blog._id}
                      className="group cursor-pointer border-b pb-3 last:border-b-0 last:pb-0"
                      onClick={() => handleViewBlog(blog)}
                    >
                      <h4 className="font-semibold text-gray-900 group-hover:text-rose-600 transition-colors line-clamp-2 mb-1">
                        {blog.title}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Eye className="size-3" />
                        <span>{blog.views || 0} views</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Related Posts */}
              {relatedBlogs.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Related Articles</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {relatedBlogs.map((blog) => (
                      <div 
                        key={blog._id}
                        className="group cursor-pointer flex gap-3"
                        onClick={() => handleViewBlog(blog)}
                      >
                        <img 
                          src={getImageUrl(blog)} 
                          alt={blog.title}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 group-hover:text-rose-600 transition-colors line-clamp-2 text-sm">
                            {blog.title}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {blog.readingTime || 5} min read
                          </p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </aside>
          </div>

          {/* Similar Posts Section at the bottom */}
          {relatedBlogs.length > 0 && (
            <div className="mt-16 pt-12 border-t">
              <h3 className="text-2xl font-bold mb-8 text-gray-900 flex items-center gap-2">
                <Heart className={`size-5 text-rose-500 fill-rose-500`} />
                Similar Articles You Might Like
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedBlogs.map((blog) => (
                  <Card 
                    key={blog._id} 
                    className="group cursor-pointer hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full bg-white border border-gray-150 rounded-xl"
                    onClick={() => {
                      handleViewBlog(blog);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <div className="relative overflow-hidden aspect-video">
                      <img 
                        src={getImageUrl(blog)} 
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <Badge className={`absolute top-3 left-3 ${currentTheme.gradient} text-white shadow-xs border-0`}>
                        {getCategoryDisplay(blog.category)}
                      </Badge>
                    </div>
                    <CardHeader className="flex-1 p-4 pb-2">
                      <CardTitle className="line-clamp-2 group-hover:text-rose-600 transition-colors text-base font-bold text-gray-900">
                        {blog.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-2 text-xs text-gray-500 mt-2">
                        {blog.excerpt}
                      </CardDescription>
                    </CardHeader>
                    <CardFooter className="p-4 pt-0 border-t bg-gray-50/30 flex items-center justify-between text-xs text-gray-400">
                      <span>{blog.readingTime || 5} min read</span>
                      <span className={`font-bold ${currentTheme.primaryText}`}>Read →</span>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const featuredPost = filteredPosts.find(post => post.isFeatured) || filteredPosts[0];
  const regularPosts = featuredPost 
    ? filteredPosts.filter(post => post._id !== featuredPost._id)
    : filteredPosts;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={isSubdomainMode ? () => window.location.href = 'https://atootbandan.com' : onBack}>
              <ArrowLeft className="size-4 mr-2" />
              {isSubdomainMode ? 'Main Website' : 'Back to Home'}
            </Button>
            
            <div className="flex items-center gap-4">
              {/* Write/Submit Buttons */}
              {user?.role === 'admin' ? (
                <Button onClick={handleOpenCreate} className={`${currentTheme.gradient} text-white`}>
                  <Plus className="size-4 mr-2" />
                  Create Blog Post
                </Button>
              ) : (
                <Dialog open={guestBlogOpen} onOpenChange={setGuestBlogOpen}>
                  <DialogTrigger asChild>
                    <Button className={`${currentTheme.gradient} text-white`}>
                      <Plus className="size-4 mr-2" />
                      Submit Guest Blog
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Submit Guest Blog</DialogTitle>
                      <DialogDescription>
                        Share your story with our community. Your submission will be reviewed by our admin team.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label htmlFor="guest-title">Title *</Label>
                        <Input
                          id="guest-title"
                          value={guestBlogForm.title}
                          onChange={(e) => setGuestBlogForm({ ...guestBlogForm, title: e.target.value })}
                          placeholder="Enter blog title"
                        />
                      </div>
                      <div>
                        <Label htmlFor="guest-excerpt">Excerpt *</Label>
                        <Textarea
                          id="guest-excerpt"
                          value={guestBlogForm.excerpt}
                          onChange={(e) => setGuestBlogForm({ ...guestBlogForm, excerpt: e.target.value })}
                          placeholder="Brief summary of your blog"
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-semibold">Content *</Label>
                        <RichTextEditor
                          value={guestBlogForm.content}
                          onChange={(html) => setGuestBlogForm({ ...guestBlogForm, content: html })}
                          placeholder="Write your fully formatted guest blog content here..."
                          minHeight="250px"
                        />
                      </div>
                      <div>
                        <Label htmlFor="guest-category">Category *</Label>
                        <Select 
                          value={guestBlogForm.category} 
                          onValueChange={(value) => setGuestBlogForm({ ...guestBlogForm, category: value })}
                        >
                          <SelectTrigger id="guest-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.filter(c => c !== 'All').map(category => (
                              <SelectItem key={category} value={category}>
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="guest-tags">Tags (comma separated)</Label>
                        <Input
                          id="guest-tags"
                          value={guestBlogForm.tags}
                          onChange={(e) => setGuestBlogForm({ ...guestBlogForm, tags: e.target.value })}
                          placeholder="e.g. love, marriage, relationships"
                        />
                      </div>
                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-3">Author Information</h4>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="guest-name">Name *</Label>
                            <Input
                              id="guest-name"
                              value={guestBlogForm.guestAuthor.name}
                              onChange={(e) => setGuestBlogForm({
                                ...guestBlogForm,
                                guestAuthor: { ...guestBlogForm.guestAuthor, name: e.target.value }
                              })}
                              placeholder="Your name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="guest-email">Email *</Label>
                            <Input
                              id="guest-email"
                              type="email"
                              value={guestBlogForm.guestAuthor.email}
                              onChange={(e) => setGuestBlogForm({
                                ...guestBlogForm,
                                guestAuthor: { ...guestBlogForm.guestAuthor, email: e.target.value }
                              })}
                              placeholder="your@email.com"
                            />
                          </div>
                          <div>
                            <Label htmlFor="guest-bio">Bio</Label>
                            <Textarea
                              id="guest-bio"
                              value={guestBlogForm.guestAuthor.bio}
                              onChange={(e) => setGuestBlogForm({
                                ...guestBlogForm,
                                guestAuthor: { ...guestBlogForm.guestAuthor, bio: e.target.value }
                              })}
                              placeholder="Brief bio about yourself"
                              rows={2}
                            />
                          </div>
                        </div>
                      </div>
                      <Button 
                        onClick={handleSubmitGuestBlog}
                        className={`w-full ${currentTheme.gradient} text-white`}
                      >
                        <Send className="size-4 mr-2" />
                        Submit for Review
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {/* Login/Logout Button for Subdomain */}
              {isSubdomainMode && (
                isAuthenticated ? (
                  <Button variant="outline" onClick={handleLogout} className="gap-2">
                    <LogOut className="size-4" />
                    Logout
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => navigate('/login')} className="gap-2">
                    <LogIn className="size-4" />
                    Blogger Login
                  </Button>
                )
              )}

              {/* Brand Logo */}
              <div className="flex items-center gap-2">
                <Heart className={`size-6 ${currentTheme.primaryText}`} />
                <span className={`text-xl font-bold ${currentTheme.primaryText}`}>Atootbandan Blog</span>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
            <Input
              placeholder="Search articles, topics, tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 shadow-sm"
            />
          </div>
        </div>
      </header>

      {/* Admin View Mode Toggles */}
      {user?.role === 'admin' && (
        <div className="bg-white border-b py-3 shadow-xs">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-600 flex items-center gap-1">
              <FileText className="size-4" /> Admin Filter:
            </span>
            <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAdminStatus('published')}
                className={`h-8 px-4 rounded-md transition-all ${adminStatus === 'published' ? 'bg-white shadow-xs font-semibold text-gray-900' : 'text-gray-500'}`}
              >
                Published
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAdminStatus('draft')}
                className={`h-8 px-4 rounded-md transition-all ${adminStatus === 'draft' ? 'bg-white shadow-xs font-semibold text-gray-900' : 'text-gray-500'}`}
              >
                Drafts
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAdminStatus('pending')}
                className={`h-8 px-4 rounded-md transition-all ${adminStatus === 'pending' ? 'bg-white shadow-xs font-semibold text-gray-900' : 'text-gray-500'}`}
              >
                Pending Submissions
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Banner */}
      <section className={`${currentTheme.light} py-12 bg-gradient-to-r from-rose-50 via-pink-50 to-rose-50 border-b`}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Relationship Insights & Matrimony Tips</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto font-medium">
            Expert advice, verified success stories, and marriage guidance for your matchmaking journey.
          </p>
        </div>
      </section>

      {/* Categories Bar */}
      <section className="bg-white border-b shadow-xs">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
            {categories.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category || (!selectedCategory && category === 'All') ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category === 'All' ? null : category)}
                className={`rounded-full transition-all ${
                  selectedCategory === category || (!selectedCategory && category === 'All') 
                    ? `${currentTheme.gradient} text-white border-0 shadow-md` 
                    : 'hover:bg-rose-50 hover:text-rose-600 hover:border-rose-300'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post Hero */}
      {!loading && featuredPost && (
        <section className="max-w-7xl mx-auto px-4 pt-8">
          <div 
            className="group cursor-pointer bg-white border border-gray-150 rounded-3xl overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 grid grid-cols-1 lg:grid-cols-12 gap-0"
            onClick={() => handleViewBlog(featuredPost)}
          >
            <div className="lg:col-span-7 relative overflow-hidden aspect-video lg:aspect-auto min-h-[300px]">
              <img 
                src={getImageUrl(featuredPost)} 
                alt={featuredPost.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-102"
              />
              <div className="absolute top-6 left-6 flex gap-2">
                <Badge className="bg-rose-600 text-white font-bold px-3 py-1 shadow-md border-0">
                  FEATURED
                </Badge>
                <Badge className={`${currentTheme.gradient} text-white font-semibold px-3 py-1 shadow-md border-0`}>
                  {getCategoryDisplay(featuredPost.category)}
                </Badge>
              </div>
            </div>
            <div className="lg:col-span-5 p-8 lg:p-12 flex flex-col justify-center">
              <span className="text-xs text-gray-400 font-semibold mb-2">
                {new Date(featuredPost.publishedAt || featuredPost.createdAt || featuredPost.date || '').toLocaleDateString('en-IN', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-rose-600 transition-colors leading-snug mb-4">
                {featuredPost.title}
              </h2>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 line-clamp-3">
                {featuredPost.excerpt}
              </p>
              <div className="flex items-center justify-between mt-auto">
                <div className="flex items-center gap-3 text-xs text-gray-500 font-medium">
                  <div className="size-8 rounded-full bg-gray-150 flex items-center justify-center text-gray-700 font-semibold border border-gray-200">
                    {getAuthorName(featuredPost).charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{getAuthorName(featuredPost)}</p>
                    <p className="text-gray-400">{featuredPost.readingTime || 5} min read</p>
                  </div>
                </div>
                <Button className={`${currentTheme.gradient} text-white font-semibold hover:shadow-lg transition-all rounded-full px-6`}>
                  Read Article →
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto mb-4"></div>
            <p className="text-gray-500 font-medium">Loading articles...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularPosts.map(post => (
                <Card 
                  key={post._id || post.id} 
                  className="group cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full bg-white border border-gray-150 rounded-xl"
                  onClick={() => handleViewBlog(post)}
                >
                  <div className="relative overflow-hidden aspect-video">
                    <img 
                      src={getImageUrl(post)} 
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <Badge className={`absolute top-4 left-4 ${currentTheme.gradient} text-white shadow-md border-0`}>
                      {getCategoryDisplay(post.category)}
                    </Badge>
                  </div>
                  
                  <CardHeader className="flex-1 pb-2">
                    <CardTitle className="line-clamp-2 group-hover:text-rose-600 transition-colors text-lg font-bold text-gray-900">
                      {post.title}
                    </CardTitle>
                    <CardDescription className="line-clamp-3 text-sm text-gray-500 mt-2 leading-relaxed">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pb-4">
                    <div className="flex items-center gap-4 text-xs text-gray-500 font-medium">
                      <div className="flex items-center gap-1">
                        <User className="size-3.5" />
                        <span className="truncate max-w-[100px]">{getAuthorName(post)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="size-3.5" />
                        <span>{post.readingTime || post.readTime || '5 min'}</span>
                      </div>
                      {post.views !== undefined && (
                        <div className="flex items-center gap-1">
                          <Eye className="size-3.5" />
                          <span>{post.views}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="border-t pt-4 bg-gray-50/50 flex items-center justify-between">
                    <span className="text-xs text-gray-400 font-semibold">{new Date(post.publishedAt || post.createdAt || post.date || '').toLocaleDateString('en-IN')}</span>
                    <span className={`text-xs font-bold ${currentTheme.primaryText} flex items-center group-hover:translate-x-1 transition-transform`}>
                      Read Article →
                    </span>
                  </CardFooter>

                  {/* Admin inline management footer */}
                  {user?.role === 'admin' && (
                    <CardFooter className="border-t pt-3 pb-3 bg-gray-50 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      {post.status === 'pending' ? (
                        <>
                          <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-8" onClick={(e) => handleApproveBlog(post._id, e)}>
                            <Check className="size-3.5 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" className="font-semibold text-xs h-8" onClick={(e) => handleRejectBlog(post._id, e)}>
                            <X className="size-3.5 mr-1" /> Reject
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="h-8 text-xs font-semibold border-rose-200 text-rose-700 hover:bg-rose-50" onClick={(e) => handleOpenEdit(post, e)}>
                            <Edit className="size-3.5 mr-1" /> Edit
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="destructive" className="h-8 text-xs font-semibold">
                                <Trash2 className="size-3.5 mr-1" /> Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this blog post? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={(e) => handleDeleteBlog(post._id, e)} className="bg-red-600 hover:bg-red-700 text-white">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </CardFooter>
                  )}
                </Card>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-16 bg-white rounded-xl shadow-xs border">
                <FileText className="size-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-lg font-medium">No blog posts found matching this view.</p>
              </div>
            )}
          </>
        )}
      </section>

      {/* Newsletter Section */}
      <section className="bg-white border-t border-b mt-16 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-rose-50 text-rose-600 mb-4">
            <Heart className="size-8 fill-rose-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Subscribe to Atootbandan Insights</h2>
          <p className="text-gray-500 max-w-lg mx-auto mb-8 text-sm md:text-base leading-relaxed">
            Get weekly expert relationship advice, wedding planning tips, and verified matchmaking success stories delivered straight to your inbox.
          </p>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              toast.success("Thank you for subscribing! Keep an eye on your inbox.");
              (e.target as HTMLFormElement).reset();
            }}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <Input 
              type="email" 
              placeholder="Enter your email address" 
              required
              className="h-12 rounded-xl border-gray-200 focus-visible:ring-rose-500 shadow-sm"
            />
            <Button 
              type="submit"
              className={`h-12 rounded-xl font-bold px-8 ${currentTheme.gradient} text-white hover:shadow-lg transition-all`}
            >
              Subscribe Now
            </Button>
          </form>
          <p className="text-xs text-gray-400 mt-4">
            We value your privacy. Unsubscribe at any time.
          </p>
        </div>
      </section>

      {/* Admin Creator Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</DialogTitle>
            <DialogDescription>
              {editingBlog ? 'Modify the details of your blog post below.' : 'Add a new blog post directly to your blogging site.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="blog-title" className="font-semibold">Title *</Label>
                <Input
                  id="blog-title"
                  value={blogForm.title}
                  onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                  placeholder="e.g. 5 Secrets of a Happy Marriage"
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="blog-category" className="font-semibold">Category *</Label>
                <Select 
                  value={blogForm.category} 
                  onValueChange={(value) => setBlogForm({ ...blogForm, category: value })}
                >
                  <SelectTrigger id="blog-category" className="h-10">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryMap).map(([label, val]) => (
                      <SelectItem key={val} value={val}>
                        {label}
                      </SelectItem>
                    ))}
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="blog-tags" className="font-semibold">Tags (comma separated)</Label>
                <Input
                  id="blog-tags"
                  value={blogForm.tags}
                  onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })}
                  placeholder="marriage, advice, dating"
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="blog-image" className="font-semibold">Featured Image URL</Label>
                <Input
                  id="blog-image"
                  value={blogForm.featuredImage}
                  onChange={(e) => setBlogForm({ ...blogForm, featuredImage: e.target.value })}
                  placeholder="https://images.unsplash.com/... or leave empty for default"
                  className="h-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="blog-excerpt" className="font-semibold">Excerpt (Short Summary)</Label>
              <Textarea
                id="blog-excerpt"
                value={blogForm.excerpt}
                onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                placeholder="A brief summary that will appear on the blog list card..."
                rows={2}
                className="resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="space-y-2">
                <Label htmlFor="blog-status" className="font-semibold">Status</Label>
                <Select 
                  value={blogForm.status} 
                  onValueChange={(value: any) => setBlogForm({ ...blogForm, status: value })}
                >
                  <SelectTrigger id="blog-status" className="h-10">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="blog-featured"
                  checked={blogForm.isFeatured}
                  onChange={(e) => setBlogForm({ ...blogForm, isFeatured: e.target.checked })}
                  className="rounded border-gray-300 text-rose-600 focus:ring-rose-500 h-4 w-4"
                />
                <Label htmlFor="blog-featured" className="cursor-pointer font-medium">Feature this post on home page</Label>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Label className="font-semibold">Blog Content *</Label>
              <RichTextEditor
                value={blogForm.content}
                onChange={(html) => setBlogForm({ ...blogForm, content: html })}
                placeholder="Write your fully featured blog content here..."
                minHeight="350px"
              />
            </div>
          </div>
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveBlog} className={`${currentTheme.gradient} text-white`}>
              {editingBlog ? 'Update Post' : 'Publish Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}